/*
 *   File : kids-education.js
 *   Author : https://evoqins.com
 *   Description : Kids education goal page
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useState, useEffect } from "react";

// *********** Third Party ***************//
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//
import { CustomAmountInput, CustomSlider, CustomTextInput, SelectDropdown } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Footer } from "../../Component/Footer";
import { BreadCrumbs } from "../../Component/Other";
import { Icon } from "../../Component/Icon";
import { ChangeBasketNameModal } from "../../Component/Modal";
import GoalSummary from "./goal-summary";
import { APIService } from "../../Service";
import { _formatLac, _formatToRupees, _getGoalRequiredSip, _setGoal } from "../../Helper";
import { _getAchieveByText } from "../../Helper/converter";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LeftPillarImage from "../../Assets/Images/GoalNew/goal-left-banner.png";
import RightPillarImage from "../../Assets/Images/GoalNew/goal-right-banner.png";

const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Life goals",
        path: "/goals"
    },
];

const DEFAULT_COURSE_ID = 3;
const DEFAULT_COURSE_COST = 500000;
const DEFAULT_CHILD_AGE = "9";
const DEFAULT_SAVED_AMOUNT = 10000;

const MAX_COURSE_COST = 20000000;
const MAX_SAVED_AMOUNT = 990000000;
const COURSE_COST_MIN = 10000;
const COURSE_COST_STEP = 10000;

// Most courses start when the child turns 18, management starts at 22
const COLLEGE_START_AGE = 18;
const MANAGEMENT_START_AGE = 22;
const MAX_CHILD_AGE = COLLEGE_START_AGE - 1;
const MIN_YEARS_TO_COURSE = 1;

// Fixed assumptions of the education corpus, the inflation is taken from the goal detail when it is available
const DEFAULT_INFLATION_RATE = 10;
const RETURN_RATE = 12;

const MIN_TIMELINE_FILL_PERCENT = 2.7;

// Selecting a course fills its cost today, the age the course starts at is used for the years to the course
const COURSE_OPTIONS = [
    {
        id: 1,
        display_name: "Engineer",
        cost: 3000000,
        start_age: COLLEGE_START_AGE,
    },
    {
        id: 2,
        display_name: "Doctor",
        cost: 10000000,
        start_age: COLLEGE_START_AGE,
    },
    {
        id: 4,
        display_name: "MBA",
        cost: 4000000,
        start_age: MANAGEMENT_START_AGE,
    },
    {
        id: DEFAULT_COURSE_ID,
        display_name: "Study Abroad",
        cost: 15000000,
        start_age: COLLEGE_START_AGE,
    },
    {
        id: 5,
        display_name: "Other",
        cost: 5000000,
        start_age: COLLEGE_START_AGE,
    },
];

// Longest time to a course start, for a child aged 0
const MAX_YEARS_TO_COURSE = Math.max(...COURSE_OPTIONS.map((course) => course.start_age));

const KidsEducation = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [goalId, setGoalId] = useState("");
    const [goalDetail, setGoalDetail] = useState({});
    const [loading, setLoading] = useState(true);

    const [selectedCourse, setSelectedCourse] = useState(COURSE_OPTIONS.find((course) => course.id === DEFAULT_COURSE_ID));
    const [courseCost, setCourseCost] = useState(DEFAULT_COURSE_COST);
    const [childAge, setChildAge] = useState(DEFAULT_CHILD_AGE);
    const [savedAmount, setSavedAmount] = useState(DEFAULT_SAVED_AMOUNT);
    const [isSavingGoal, setIsSavingGoal] = useState(false);
    const [savedGoalId, setSavedGoalId] = useState(null);
    const [goalName, setGoalName] = useState("");
    const [showChangeNameModal, setShowChangeNameModal] = useState(false);
    const [isNameUpdating, setIsNameUpdating] = useState(false);
    const [goalSummary, setGoalSummary] = useState(false);

    useEffect(() => {

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (location.state !== null) {
            setGoalId(location.state.goal_id);
        } else {
            navigate("/goals");
        };

        if (location.state?.is_summary === false) {
            setGoalSummary(false);
        }

    }, [location]);

    useEffect(() => {
        if (goalId !== "") {
            _getGoalDetails();
        }
    }, [goalId]);

    // The summary step is kept in the url, so that a reload stays on it
    useEffect(() => {
        if (goalSummary === true) {
            navigate(`${location.pathname}#configure`, {
                state: {
                    goal_id: goalId,
                }
            });
        }
    }, [goalSummary]);

    useEffect(() => {
        if (showChangeNameModal) {
            new Modal(document.getElementById("change-basket-name-modal")).show();
        }
    }, [showChangeNameModal]);

    const breadcrumbs = [...BREADCRUMBS, { label: goalDetail.admin_configured?.name }];

    const courseCostAmount = Number(courseCost) || 0;
    const savedAmountValue = Number(savedAmount) || 0;
    const inflationRate = goalDetail.admin_configured?.meta_data?.inflation_rate ?? DEFAULT_INFLATION_RATE;

    // The course cost today grows with the inflation until the child starts the course
    const yearsToCourse = Math.max(selectedCourse.start_age - (Number(childAge) || 0), MIN_YEARS_TO_COURSE);
    const goalCost = Math.round(courseCostAmount * Math.pow(1 + inflationRate / 100, yearsToCourse));
    const monthlySip = _getGoalRequiredSip(goalCost, savedAmountValue, RETURN_RATE, yearsToCourse);

    // The timeline fills as the saved amount grows towards the goal cost
    let savedProgressPercent = 0;
    if (goalCost > 0) {
        savedProgressPercent = Math.min((savedAmountValue / goalCost) * 100, 100);
    }
    const timelineFillPercent = Math.max(savedProgressPercent, MIN_TIMELINE_FILL_PERCENT);

    const goalCostText = _formatLac(goalCost).replace(" ", "");
    const achieveByText = _getAchieveByText(yearsToCourse);

    //handle back
    const _handleBack = () => {
        navigate("/goals");
    };

    const _handleSelectCourse = (course) => {
        setSelectedCourse(course);
        setCourseCost(course.cost);
    };

    const _handleCourseCostChange = (value) => {
        setCourseCost(value);
    };

    // The text input gives a string and the slider a number
    const _handleChildAgeChange = (value) => {
        setChildAge(String(value));
    };

    const _handleSavedAmountChange = (value) => {
        setSavedAmount(value);
    };

    const _handleChangeGoalName = (name) => {
        _renameGoal(name, false);
    };

    const _handleSkipGoalName = () => {
        _renameGoal("", true);
    };

    const _handleCloseChangeNameModal = () => {
        setIsNameUpdating(false);
        setShowChangeNameModal(false);
    };

    // API - Rename goal
    const _renameGoal = (name, skip) => {
        let api_version = 3;
        let is_post = true;
        let url = "goal/rename";
        let data = {
            "goal_id": savedGoalId,
            "skip": true,
        };

        if (skip === false) {
            data = {
                "goal_id": savedGoalId,
                "name": name,
                "skip": false,
            };
        }

        setIsNameUpdating(true);

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                if (skip === false) {
                    setGoalName(name);
                }
                document.getElementById("change-basket-name-modal-close")?.click();
                setGoalSummary(true);
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsNameUpdating(false);
        });
    };

    // API - Save the kids education goal, the saved amount goes as the lumpsum
    const _handleSaveGoal = () => {
        if (courseCostAmount === 0) {
            toast.dismiss();
            toast.error("Enter the cost of the course", { className: "e-toast" });
            return;
        }

        if (childAge === "") {
            toast.dismiss();
            toast.error("Enter your child’s age", { className: "e-toast" });
            return;
        }

        if (savedAmountValue > goalCost) {
            toast.dismiss();
            toast.error("Goal cost should be greater than the amount saved", { className: "e-toast" });
            return;
        }

        const params = {
            "goal_id": goalId,
            "target": goalCost,
            "sip": monthlySip || null,
            "lumpsum": savedAmountValue || null,
            "sip_tenure": yearsToCourse,
            "lumpsum_select": 1,
            // The goal cost is already worked out in the inflated value of the year the course starts
            "inflation_select": false,
            "additional_data": {
                "add_lumpsum_now": savedAmountValue > 0,
                "course_id": selectedCourse.id,
                "course_cost": courseCostAmount,
                "child_age": Number(childAge),
                "return_rate": RETURN_RATE,
            },
        };

        setIsSavingGoal(true);

        _setGoal(params).then((response) => {
            if (response.status_code === 200) {
                setSavedGoalId(response.data.goal_id);
                setShowChangeNameModal(true);
                toast.dismiss();
                toast.success("Goal saved", { className: "e-toast" });
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsSavingGoal(false);
        });
    };

    //API- Post goal detail
    const _getGoalDetails = () => {
        let is_post = true;
        let url = 'goal/get';
        let data = {
            "goal_id": goalId,
            "is_invested": false
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setGoalDetail(response.data);
                _fillGoalInputs(response.data);

                // Preserving current step if user reloads page
                if (location.hash) {
                    setGoalSummary(true);
                }
            } else {
                setGoalDetail({});
            }
            setLoading(false);
        });
    };

    // Fills the inputs from the saved goal (user_configured), the rest fall back to the defaults
    const _fillGoalInputs = (goal) => {
        const user_configured = goal.user_configured || {};
        const saved_inputs = user_configured.additional_data || {};
        const saved_course = COURSE_OPTIONS.find((course) => course.id === saved_inputs.course_id);

        if (saved_course) {
            setSelectedCourse(saved_course);
        }
        setCourseCost(saved_inputs.course_cost || DEFAULT_COURSE_COST);
        setChildAge(String(saved_inputs.child_age ?? DEFAULT_CHILD_AGE));
        setSavedAmount(user_configured.lumpsum || DEFAULT_SAVED_AMOUNT);
    };

    // Stays rendered on the summary step until the modal has finished closing
    const changeNameModal = showChangeNameModal &&
        <ChangeBasketNameModal title="Change goal name"
            skipButtonName="Skip"
            basketName={goalName || goalDetail.admin_configured?.name || ""}
            loading={isNameUpdating}
            onChangeName={_handleChangeGoalName}
            onSkip={_handleSkipGoalName}
            onClose={_handleCloseChangeNameModal} />;

    // Goal summary step
    if (goalSummary) {
        return (
            <>
                <GoalSummary goal_name={goalDetail.admin_configured?.short_name}
                    savedGoalId={savedGoalId}
                    customGoalName={goalName}
                    isLumpsum={savedAmountValue > 0}
                    tenure={{ min: MIN_YEARS_TO_COURSE, max: MAX_YEARS_TO_COURSE }}
                    inflationSelect={false} />

                {changeNameModal}

                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">

                {loading ?
                    <div className="min-h-100vh text-center mt-5" >
                        <img src={Loader}
                            draggable="false"
                            alt="loader" />
                    </div>
                    :
                    <div className="col-lg-11 p-0 pb-4 pb-sm-5">
                        {/* breadcrumbs */}
                        <BreadCrumbs data={breadcrumbs} />

                        {/* Page title */}
                        <header className="d-flex align-items-center gap-sm-3 gap-2 my-sm-4 my-3 ">
                            <button type="button"
                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                aria-label="Back to life goals"
                                onClick={_handleBack}>
                                <Icon icon="arrow-back-left" size={24} className="width-20px-res" />
                            </button>
                            <h1 className="mb-0 e-text-chinese-black e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                {goalDetail.admin_configured?.name}
                            </h1>
                        </header>

                        <div className="row gx-4 gy-4 align-items-start">

                            {/* Kids education plan */}
                            <div className="col-md-6">
                                <section className="retirement-goal-card e-bg-white position-relative overflow-hidden pb-3">
                                    <img src={LeftPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-left d-none d-sm-block"
                                        draggable="false"
                                        alt="" />
                                    <img src={RightPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-right d-none d-sm-block"
                                        draggable="false"
                                        alt="" />

                                    <div className="retirement-goal-content position-relative px-3 px-sm-4">
                                        <header className="px-0 px-sm-5 pt-3 text-center">
                                            <h2 className="mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-30px">
                                                Secure your child’s <span className="e-heading-font-ivy e-font-weight-400 e-text-cobalt-ink">education</span>.
                                            </h2>
                                            <p className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-13 e-font-12-res">
                                                Plan for future expenses and give your child the best opportunities to succeed.
                                            </p>
                                        </header>

                                        {/* Projection */}
                                        <article className="retirement-projection e-border-1px-baby-blue e-border-radius-28 p-3 p-sm-4 mt-4">
                                            <div className="d-flex justify-content-between gap-3">
                                                <div>
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Invest
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{_formatToRupees(monthlySip)}
                                                        <span className="ms-1 e-text-jet-gray e-font-weight-500 e-font-12 line-height-16px">
                                                            monthly
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Goal cost
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{goalCostText}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="retirement-timeline mt-4"
                                                role="progressbar"
                                                aria-label="Saved amount compared to the goal cost"
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                                aria-valuenow={Math.round(savedProgressPercent)}>
                                                <span className="retirement-timeline-fill"
                                                    style={{ width: `${timelineFillPercent}%` }}></span>
                                            </div>
                                            <p className="d-flex justify-content-between gap-2 mt-2 mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-14 e-font-12-res line-height-18px">
                                                <span>Start today</span>
                                                <span>
                                                    Achieve by <span className="e-text-ink-gray e-font-weight-500">{achieveByText}</span>
                                                </span>
                                            </p>
                                        </article>
                                    </div>
                                </section>

                                {/* Inputs */}
                                <div className="d-flex flex-column gap-4 mt-3 px-4">
                                    <div>
                                        <label htmlFor="kids-education-course"
                                            className="d-block mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                            What will your child study?
                                        </label>
                                        <SelectDropdown id="kids-education-course"
                                            data={COURSE_OPTIONS}
                                            selectedOption={selectedCourse}
                                            onSelectOption={_handleSelectCourse} />
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Cost of the course today
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={courseCost}
                                                maxValue={MAX_COURSE_COST}
                                                errorMsg=""
                                                handleChange={_handleCourseCostChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={COURSE_COST_MIN}
                                                max={MAX_COURSE_COST}
                                                step={COURSE_COST_STEP}
                                                defaultValue={courseCostAmount}
                                                handleChange={_handleCourseCostChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Your child’s age today
                                            </span>
                                            <CustomTextInput variant={4}
                                                type="number"
                                                suffix="Year(s)"
                                                className="text-end"
                                                wrapperClassName="w-190px flex-shrink-0"
                                                wrapperPadding="padding-12px-lr"
                                                value={childAge}
                                                maxLength={2}
                                                maxValue={MAX_CHILD_AGE}
                                                hasBottomMargin={false}
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                inputWrapperBorderStyle="e-border-radius-12"
                                                errorMsg=""
                                                successMsg=""
                                                handleChange={_handleChildAgeChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={0}
                                                max={MAX_CHILD_AGE}
                                                step={1}
                                                defaultValue={Number(childAge) || 0}
                                                handleChange={_handleChildAgeChange} />
                                        </div>
                                    </div>

                                    <label className="d-flex justify-content-between align-items-center gap-2">
                                        <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                            Saved for this already
                                        </span>
                                        <CustomAmountInput variant={4}
                                            className="w-190px flex-shrink-0"
                                            inputClassName="e-bg-white"
                                            borderClassName="e-border-1px-gainsboro-gray"
                                            value={savedAmount}
                                            maxValue={MAX_SAVED_AMOUNT}
                                            errorMsg=""
                                            handleChange={_handleSavedAmountChange} />
                                    </label>

                                    <PrimaryButton name="Save this goal"
                                        className="w-100 padding-12px-top padding-12px-bottom margin-top-32px"
                                        loading={isSavingGoal}
                                        disabled={isSavingGoal}
                                        onPress={_handleSaveGoal} />
                                </div>



                            </div>
                        </div>
                    </div>
                }
            </div>

            {changeNameModal}

            {/* footer  */}
            <Footer />
        </>
    );
};
export default KidsEducation;
